import React, { useEffect, useRef, useLayoutEffect, useState } from 'react';
import { AdvancedPlanTile } from './AdvancedPlanTile';
import { IconChevronRight, IconChevronLeft, Toast,
  TOAST_VARIANTS,
  TOAST_POSITIONS, PrimaryButton } from 'cdk-radial';
import { PLAN_TYPE, FREE_TRIAL_WARNING_MESSAGE, getPlanCardText } from '../../utils/api-pricing';
import { API_DIRECTORY_THEME, API_VISIBILITY } from '../../utils/constants';

const PLAN_TYPE_ORDER = {
  custom: 1,
  private: 2,
  basic: 3,
  essentials: 4,
  plus: 5,
  pro: 6
};

const MAX_PLANS_FOR_CAROUSEL = 5;

export const AdvancedPlans = ({
  instanceIdVersionMap,
  plans,
  isAsync,
  allEndpoints,
  belongsTo,
  prodInstance,
  onCompareNowClick,
  isAdvancedPlan
}) => {
  const [showArrows, setShowArrows] = useState(false);
  const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
  const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
  const [indexStart, setIndexStart] = useState(null);
  const [plansToRender, setPlansToRender] = useState([]);
  const [width, setWidth] = useState(0);
  const [maxDisplay, setMaxDisplay] = useState(4);
  const gridStyle = plans.length >= MAX_PLANS_FOR_CAROUSEL ? 'repeat(5, 1fr)' : `repeat(${plans.length}, 1fr)`;
  const ref = useRef();
  const PLAN_CARD_TEXT = getPlanCardText(API_DIRECTORY_THEME.ISV_API_DIRECTORY);
  
  useLayoutEffect(() => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  }, []);

  // Set max to display based on how many plans can fit in the container
  useEffect(() => {
    const tileWidth = 256;
    const tileMargin = 10;
    if (width && tileWidth && tileMargin) {
      // width + left margin + right margin + little wiggle room
      const totalTileWidth = tileWidth + tileMargin + tileMargin + 2;
      const tilesToDisplay = Math.floor(width / totalTileWidth);
      setMaxDisplay(tilesToDisplay);
    }
  }, [width]);

  useEffect(() => {
  // sort based on custom, private, basic, essentials, plus, pro  and then other plans order
  plans.sort((a, b) => {
    const planA = a.visibility === API_VISIBILITY.PRIVATE ? API_VISIBILITY.PRIVATE : a.plan?.toLowerCase();
    const planB = b.visibility === API_VISIBILITY.PRIVATE ? API_VISIBILITY.PRIVATE : b.plan?.toLowerCase();

    if (PLAN_TYPE_ORDER[planA] && PLAN_TYPE_ORDER[planB]) {
      return PLAN_TYPE_ORDER[planA] - PLAN_TYPE_ORDER[planB];
    }
    return 1; // // someone added a new plan type that isn't on the defined list
  });

    // Set arrows
    let length = plans.length || 0;
    let isMoreThanMaxPlans = length > maxDisplay ? true : false;
    setIndexStart(0);
    setShowArrows(isMoreThanMaxPlans);
    setIsRightArrowDisabled(!isMoreThanMaxPlans);
  }, [plans, maxDisplay]);

  useEffect(() => {
    let plansToRender = plans.slice(indexStart, indexStart + maxDisplay);
    setPlansToRender(plansToRender);
    if (indexStart + maxDisplay >= plans.length) {
      setIsRightArrowDisabled(true);
    } else {
      setIsRightArrowDisabled(false);
    }
    setIsLeftArrowDisabled(indexStart === 0 ? true : false);
  }, [indexStart, maxDisplay]);

  const areAllPlansAdvanced = plans?.length > 0 && plans.every((plan) => plan?.type?.toLowerCase() === PLAN_TYPE.ADVANCED);

  return (
    <>
    {areAllPlansAdvanced && (
    <>
      <div className="api-dir-pkg_rate-plan container">
        <div className="api-dir-pkg_rate-plan api-dir-pkg_heading">
          {PLAN_CARD_TEXT[PLAN_TYPE.ADVANCED].heading}
        </div>
        <div className="api-dir-pkg_advance-plan-divider"></div>
      </div>
      {isAdvancedPlan && prodInstance && (
        <div className="api-dir-pkg_pricing-calculator">
          <div className="api-dir-pkg_content-frame">
            <div className="api-dir-pkg_content-frame-message">
              <div className="api-dir-pkg_text-wrapper">
                Compare plans with our pricing calculator{" "}
              </div>
              <div className="api-dir-pkg_message-frame">
                Estimate your costs with the pricing calculator. See how plans compare based on your API usage.
              </div>
            </div>
            <div>
              <PrimaryButton
                iconColor="primary"
                text="Compare Now"
                onClick={onCompareNowClick}
              />
            </div>
          </div>
        </div>
      )}
      </>
      )}
      <div className="api-dir-pkg_advance-plans-container" ref={ref}>
        <div className="api-dir-pkg_advance-pricing-carousel">
          {showArrows && (
            <div className="api-dir-pkg_arrow-left">
              <button
                className={`api-dir-pkg_arrow-button ${isLeftArrowDisabled &&
                  "api-dir-pkg_arrow-disabled"}`}
                onClick={() => {
                  setIndexStart(indexStart - maxDisplay);
                }}
                disabled={isLeftArrowDisabled}
              >
                <IconChevronLeft
                  height={24}
                  width={24}
                  data-testid="chevron-left"
                />
              </button>
            </div>
          )}
          <div
            className={`api-dir-pkg_advance-plans ${
              plans.length < MAX_PLANS_FOR_CAROUSEL ? "no-carousel" : ""
            }`}
            style={{
              gridTemplateColumns: `${gridStyle}`,
              MsGridColumns: `${gridStyle}`,
            }}
          >
            {plansToRender
              .map((p, idx) => (
                <AdvancedPlanTile
                  key={idx}
                  plan={p}
                  isAsync={isAsync}
                  allEndpoints={allEndpoints}
                  instanceIdVersionMap={instanceIdVersionMap}
                  belongsTo={belongsTo}
                />
              ))}
          </div>
          {showArrows && (
            <div className="api-dir-pkg_arrow-right">
              <button
                className={`api-dir-pkg_arrow-button ${isRightArrowDisabled &&
                  "api-dir-pkg_arrow-disabled"}`}
                onClick={() => {
                  setIndexStart(indexStart + maxDisplay);
                }}
                disabled={isRightArrowDisabled}
              >
                <IconChevronRight
                  height={24}
                  width={24}
                  data-testid="chevron-right"
                />
              </button>
            </div>
          )}
        </div>
        <Toast
          style={{ width: "864px" }}
          content={FREE_TRIAL_WARNING_MESSAGE}
          position={TOAST_POSITIONS.INLINE}
          variant={TOAST_VARIANTS.WARNING}
        />
      </div>
    </>
  );
};
